import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { Box, Button, DriftCircularProgress, Typography } from "drift-ui";
import { useState } from "react";
import { safeReturnPath } from "./forthLeafNavigation";
import { ForthLeafFeatureProvider } from "./forthLeafFeatures";
import { useForthLeafSession } from "./forthLeafSession";
import {
  createForthLeafRouter,
  getForthLeafPermissionRequirements,
  queryClient,
  router as appRouter,
} from "./forthLeafRouter";

interface AppProps {
  router?: ReturnType<typeof createForthLeafRouter>;
}

const AppLoading = () => (
  <Box className="app-loading-container">
    <DriftCircularProgress />
    <Typography>Opening Forth Leaf...</Typography>
  </Box>
);

const ForthLeafApp = ({ router = appRouter }: AppProps) => {
  const { getAccessTokenSilently, getIdTokenClaims, logout } = useAuth0();
  const { identity, retry, status } = useForthLeafSession();
  const [startedOnProtectedRoute] = useState(
    () => getForthLeafPermissionRequirements(router, router.state.location.pathname).length > 0,
  );

  if (status === "loading") {
    return <Typography>Checking your Forth Leaf permission...</Typography>;
  }

  if (status === "denied" && !startedOnProtectedRoute) {
    return (
      <Box>
        <Typography>You do not have access to Forth Leaf.</Typography>
        <Typography>Contact the Forth Leaf application owner for assistance.</Typography>
        <Button
          onClick={() => {
            // Auth0 navigates away after sign-out, so its completion is intentionally not awaited.
            void logout({ logoutParams: { returnTo: window.location.origin } });
          }}
        >
          Sign out
        </Button>
      </Box>
    );
  }

  if (status === "error") {
    return (
      <Box>
        <Typography>We could not verify your Forth Leaf permission.</Typography>
        <Button onClick={retry}>Retry</Button>
      </Box>
    );
  }

  return (
    <ForthLeafFeatureProvider
      getAccessTokenSilently={() => getAccessTokenSilently()}
      identity={identity}
    >
      <RouterProvider
        context={{ getAccessTokenSilently, getIdTokenClaims, queryClient }}
        router={router}
      />
    </ForthLeafFeatureProvider>
  );
};

const ForthLeafQueryClientApp = (props: AppProps) => (
  <QueryClientProvider client={queryClient}>
    <ForthLeafApp {...props} />
  </QueryClientProvider>
);

export const App = withAuthenticationRequired(ForthLeafQueryClientApp, {
  onRedirecting: AppLoading,
  returnTo: () =>
    safeReturnPath(window.location.pathname, window.location.search, window.location.hash),
});
